import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { ModalWrapper } from "src/pages/configurator/components/ModalWrapper";

type MaxViewContextValue = {
  isMaxViewOpen: boolean;
  openMaxViewModal: (content: ReactNode) => void;
};

export const MaxViewContext = createContext<MaxViewContextValue | undefined>(undefined);

export type MaxViewProviderProps = {
  children: ReactNode;
  skipModal?: boolean; // Skip rendering ModalWrapper for screenshot/headless contexts
};

export function MaxViewProvider(props: MaxViewProviderProps) {
  const { children, skipModal = false } = props;
  const [isMaxViewOpen, setIsMaxViewOpen] = useState(false);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);

  const closeMaxView = useCallback(() => {
    setIsMaxViewOpen(false);
    // Clear content after close animation completes
    setTimeout(() => setModalContent(null), 300);
  }, []);

  const openMaxViewModal = useCallback((content: ReactNode) => {
    setModalContent(content);
    setIsMaxViewOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      isMaxViewOpen,
      openMaxViewModal,
    }),
    [isMaxViewOpen, openMaxViewModal],
  );

  return (
    <MaxViewContext.Provider value={value}>
      {children}
      {!skipModal && (
        <ModalWrapper isOpen={isMaxViewOpen} close={closeMaxView} maxView>
          {modalContent}
        </ModalWrapper>
      )}
    </MaxViewContext.Provider>
  );
}

export function useMaxView(): MaxViewContextValue {
  const context = useContext(MaxViewContext);
  if (context === undefined) {
    throw new Error("useMaxView must be used within a MaxViewProvider");
  }
  return context;
}
